import React from 'react';
import { useTranslation } from 'react-i18next';
import Icon from '../../../../components/AppIcon';
import { format, parseISO } from 'date-fns';

export interface TimelineItem {
  id: string;
  type: 'milestone' | 'evidence' | 'feedback' | 'checkin' | 'endorsement';
  icon: string;
  title: string;
  description: string;
  date: string;
  author?: string;
  attachments?: string[];
}

interface EvidenceTimelineProps {
  items: TimelineItem[];
  onItemClick?: (item: TimelineItem) => void;
}

const EvidenceTimeline: React.FC<EvidenceTimelineProps> = ({ items, onItemClick }) => {
  const { t } = useTranslation();

  const getTypeConfig = (type: string) => {
    const configs: Record<string, { bgClass: string; textClass: string; borderClass: string }> = {
      milestone: { bgClass: 'bg-blue-100 dark:bg-blue-900/30', textClass: 'text-blue-600 dark:text-blue-400', borderClass: 'border-blue-300 dark:border-blue-700' },
      evidence: { bgClass: 'bg-amber-100 dark:bg-amber-900/30', textClass: 'text-amber-600 dark:text-amber-400', borderClass: 'border-amber-300 dark:border-amber-700' },
      feedback: { bgClass: 'bg-green-100 dark:bg-green-900/30', textClass: 'text-green-600 dark:text-green-400', borderClass: 'border-green-300 dark:border-green-700' },
      checkin: { bgClass: 'bg-purple-100 dark:bg-purple-900/30', textClass: 'text-purple-600 dark:text-purple-400', borderClass: 'border-purple-300 dark:border-purple-700' },
      endorsement: { bgClass: 'bg-emerald-100 dark:bg-emerald-900/30', textClass: 'text-emerald-600 dark:text-emerald-400', borderClass: 'border-emerald-300 dark:border-emerald-700' }
    };
    return configs[type] || configs.milestone;
  };

  return (
    <div className="space-y-4">
      {items?.map((item, index) => {
        const config = getTypeConfig(item.type);
        return (
          <div
            key={item.id}
            className="relative transition-opacity duration-300"
            style={{ animationDelay: `${index * 100}ms` }}
          >
            {/* Timeline Line */}
            {index < items.length - 1 && (
              <div className="absolute start-5 top-12 w-0.5 h-full bg-border" />
            )}

            {/* Timeline Item */}
            <div
              className="flex items-start gap-3 cursor-pointer hover:bg-muted p-3 rounded-lg transition-colors duration-200"
              onClick={() => onItemClick?.(item)}
            >
              {/* Icon */}
              <div className={`w-10 h-10 rounded-full ${config.bgClass} border-2 ${config.borderClass} flex items-center justify-center flex-shrink-0 z-10 bg-card`}>
                <Icon name={item.icon as any} size={18} className={config.textClass} />
              </div>

              {/* Content */}
              <div className="flex-1 min-w-0">
                <div className="flex items-start justify-between mb-1">
                  <div className="flex-1">
                    <h4 className="text-sm font-semibold text-foreground">{item.title}</h4>
                    <p className="text-xs text-muted-foreground">
                      {format(parseISO(item.date), 'MMMM d, yyyy')}
                    </p>
                  </div>
                  <span className={`inline-flex items-center px-2 py-0.5 rounded-full text-xs font-medium ${config.bgClass} ${config.textClass} capitalize`}>
                    {item.type}
                  </span>
                </div>

                <p className="text-sm text-foreground leading-relaxed mb-1">{item.description}</p>

                {item.author && (
                  <div className="flex items-center gap-1 text-xs text-muted-foreground">
                    <Icon name="User" size={12} />
                    <span>{item.author}</span>
                  </div>
                )}

                {item.attachments && item.attachments.length > 0 && (
                  <div className="flex items-center gap-1 mt-1">
                    <Icon name="Paperclip" size={12} className="text-muted-foreground" />
                    <span className="text-xs text-muted-foreground">
                      {item.attachments.length} attachment{item.attachments.length > 1 ? 's' : ''}
                    </span>
                  </div>
                )}
              </div>
            </div>
          </div>
        );
      })}

      {(!items || items.length === 0) && (
        <div className="text-center py-8">
          <Icon name="Clock" size={32} className="mx-auto text-muted-foreground/50 mb-2" />
          <p className="text-sm text-muted-foreground">{t('No evidence recorded yet')}</p>
        </div>
      )}
    </div>
  );
};

export default EvidenceTimeline;
